import { InitializeTranslationService } from "src/app/core/_base/translation/initialize-translation.service";
import { PeriodValue } from "src/app/views/pages/construction-costs/types";
import { PropertyConstructionCostHeaderRow, PropertyConstructionCostRecordRow, PropertyConstructionCostValueRow } from "src/app/views/pages/property-construction-costs/types";

export function calculateGroups(
  rows: PropertyConstructionCostRecordRow[],
  propertySize: number
) {
  const level = determineLevel(rows)

  if (level == 1 || level == 2) {
    return rows.map(row => {
      if (row.kind === 'header') return row;
      return {
        ...row,
        ...calculateTotalCostAndCostPerUnit(row, propertySize)
      }
    })
  }

  const tree = buildCodeTree(rows)
  const updatedTree1 = applyUnit17FromBaseline(tree, '1.01') 
  const updatedTree = applyUnits12to15FromRoot(updatedTree1) 
  const sortedTree = sortTree(updatedTree)
  const newRows = flattenTree(sortedTree)
  return newRows.map(row => {
    if (row.kind === 'header') return row;
    return {
      ...row,
      ...calculateTotalCostAndCostPerUnit(row, propertySize)
    }
  })
}

export function determineLevel<T>(rows: T[]) {
  if (rows.length == 1) return 1
  if (rows.length == 2) return 2
  if (rows.length == 17) return 3
  return 4
}

function calculateTotalCostAndCostPerUnit(row: PropertyConstructionCostValueRow, propertySize: number): {
  totalCost: number,
  costPerUnit: number
} {
  let lastPeriodValue = 0
  if (row.periodValues.length >= 1) {
    lastPeriodValue = row.periodValues[row.periodValues.length - 1].value
  }

  const totalCost = lastPeriodValue * row.quantity
  const costPerUnit = propertySize
    ? totalCost / propertySize
    : undefined

  return {
    totalCost,
    costPerUnit
  }
}

type TreeNode = Readonly<{
  header?: PropertyConstructionCostHeaderRow
  values: PropertyConstructionCostValueRow[],
  children: Record<string, TreeNode>
}>
type Tree = Record<string, TreeNode>
const emptyNode: TreeNode = {values: [], children: {}}

const dotNumCompare = (a: string, b: string) => {
  const A = a.split('.').map(Number)
  const B = b.split('.').map(Number)
  for (let i = 0; i < Math.max(A.length, B.length); i++) {
    const x = A[i] ?? -Infinity
    const y = B[i] ?? -Infinity
    if (x !== y) return x - y
  }
  return 0
}

const setChild = (node: TreeNode, key: string, child: TreeNode): TreeNode => ({
  ...node,
  children: {...node.children, [key]: child}
})

const upsertNode = (tree: Tree, code: string, fn: (n: TreeNode) => TreeNode): Tree => {
  const parts = code.split('.')
  const walk = (t: Tree, idx: number, acc: string, parentNode?: TreeNode): [Tree, TreeNode] => {
    const key = acc ? `${acc}.${parts[idx]}` : parts[idx]
    const current = (t[key] ?? emptyNode)
    if (idx === parts.length - 1) {
      const updated = fn(current)
      return [{...t, [key]: updated}, updated]
    }
    const [updatedChildren, childNode] = walk(current.children, idx + 1, key, current)
    const updatedCurrent = setChild(current, key + '.' + parts[idx + 1], childNode)
    const updatedTree = {...t, [key]: updatedCurrent}
    return [updatedTree, updatedCurrent]
  }
  const [updated] = walk(tree, 0, '')
  return updated
}

const addHeader = (tree: Tree, item: PropertyConstructionCostHeaderRow): Tree =>
  upsertNode(tree, item.code, (n) => ({...n, header: item}))

const addValue = (tree: Tree, item: PropertyConstructionCostValueRow): Tree => {
  const parentCode = item.code.split('.').slice(0, -1).join('.')
  return upsertNode(tree, parentCode, (n) => ({...n, values: [...n.values, item]}))
}

const buildCodeTree = (items: ReadonlyArray<PropertyConstructionCostRecordRow>): Tree => 
  items.reduce<Tree>(
    (tree, item) => item.kind === 'header' ? addHeader(tree, item) : addValue(tree, item),
    {}
  )

const sortTree = (tree: Tree): Tree => {
  const sortedKeys = Object.keys(tree).sort(dotNumCompare)

  const result: Tree = {}
  for (const k of sortedKeys) {
    const node = tree[k]
    result[k] = {
      header: node.header,
      values: [...node.values].sort((a, b) => dotNumCompare(a.code, b.code)),
      children: sortTree(node.children)
    }
  }
  return result
}

const findNode = (tree: Tree, code: string): TreeNode | undefined => {
  if (tree[code]) return tree[code]
  for (const k in tree) {
    const node = tree[k]
    const hit = (node.values || []).find(v => v.code === code)
    if (hit) {
      return {header: undefined, values: [hit], children: {}}
    }
    const found = findNode(tree[k].children, code)
    if (found) return found
  }
  return undefined
}

const addSeries = (a: number[], b: number[]): number[] => {
  const len = Math.max(a.length, b.length)
  const out = new Array(len)
  for (let i = 0; i < len; i++) out[i] = (a[i] || 0) + (b[i] || 0);
  return out
}

const pluckValues = (pv?: PeriodValue[]): number[] =>
  (pv || []).map(x => (x && typeof x.value === 'number') ? x.value : 0)

const scaleSeries = (base: number[], factor: number) : number[] =>
  base.map(v => v * factor)

const toPeriodValues = (series: number[], premiumTemplate?: PeriodValue[]): PeriodValue[] =>
  series.map((v, i) => ({ value: v, premium: premiumTemplate && premiumTemplate[i] ? premiumTemplate[i].premium : null }))

const sumDescendantValues = (node?: TreeNode): number[] => {
  if (!node) return []
  // Sum this node's direct values
  const selfSeries = (node.values || []).reduce<number[]>(
    (acc, item) => addSeries(acc, pluckValues(item.periodValues)),
    []
  )

  // Sum children
  const childKeys = Object.keys(node.children || {})
  const childrenSeries = childKeys.reduce<number[]>(
    (acc, k) => addSeries(acc, sumDescendantValues(node.children[k])),
    []
  )

  return addSeries(selfSeries, childrenSeries)
}

const applyUnit17FromBaseline = (tree: Tree, baselineCode: string = '1.01'): Tree => {
  const baselineNode = findNode(tree, baselineCode)
  const baselineSeries = sumDescendantValues(baselineNode)
  if (!baselineNode || baselineSeries.length == 0) {
    return tree
  }

  const mapTree = (t: Tree): Tree => {
    const out: Tree = {}
    const keys = Object.keys(t)
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const node = t[key]
      const newValues = (node.values || []).map(item => {
        if (item.unit === 17 && typeof item.quantity === 'number') {
          const factor = item.quantity / 100
          const series = scaleSeries(baselineSeries, factor)
          return {...item, periodValues: toPeriodValues(series, item.periodValues)}
        }
        return item
      })
      out[key] = {
        header: node.header,
        values: newValues,
        children: mapTree(node.children || {})
      }
    }
    return out
  }
  return mapTree(tree)
}

const flattenTree = (tree: Tree): PropertyConstructionCostRecordRow[] => {
  const result: PropertyConstructionCostRecordRow[] = []
  const walk = (t: Tree) => {
    const keys = Object.keys(t).sort(dotNumCompare)
    for (const key of keys) {
      const node = t[key]
      if (node.header) {
        result.push(node.header)
      }
      const sortedValues = [...node.values].sort((a, b) => dotNumCompare(a.code, b.code))
      for (const val of sortedValues) {
        result.push(val)
      }
      walk(node.children)
    }
  }
  walk(tree)
  return result
}

const startsWithAny = (code: string, prefixes: string[]) => {
  for (let i = 0; i < prefixes.length; i++) {
    if (code.startsWith(prefixes[i])) return true
  }
  return false
}

const sumDescendantValuesFiltered = (
  node: TreeNode | undefined,
  excludedUnits: Set<number>,
  excludedPrefixes: string[]
): number[] => {
  if (!node) return []

  const selfSeries = (node.values || []).reduce<number[]>((acc, item) => {
    const u = item.unit as number | undefined
    if (u != null && excludedUnits.has(u)) return acc
    if (startsWithAny(item.code, excludedPrefixes)) return acc
    return addSeries(acc, pluckValues(item.periodValues))
  }, [])

  const children = node.children || {}
  const childKeys = Object.keys(children)
  const childrenSeries = childKeys.reduce<number[]>((acc, key) => {
    if (startsWithAny(key, excludedPrefixes)) return acc
    return addSeries(acc, sumDescendantValuesFiltered(children[key], excludedUnits, excludedPrefixes))
  }, [])

  return addSeries(selfSeries, childrenSeries)
}

export const applyUnits12to15FromRoot = (
  tree: Tree,
  opts?: {
    rootCode?: string,
    perUnitExclusions?: Record<number, string[]>
  }
): Tree => {
  const rootCode = opts?.rootCode ?? '2'
  const perUnitExclusions = opts?.perUnitExclusions ?? {
    13: ['2.08', '2.10'],
    14: ['2.08', '2.09'],
    15: ['2.09', '2.10'],
    16: ['2.13'],
  }
  const excludedUnits = new Set<number>([12, 13, 14, 15])

  const baselineRoot = tree[rootCode]
  if (!baselineRoot) return tree

  const baselineByUnit: Record<number, number[]> = {};
  [13, 14, 15, 16].forEach(u => {
    const prefix = perUnitExclusions[u] ?? []
    baselineByUnit[u] = sumDescendantValuesFiltered(baselineRoot, excludedUnits, prefix)
  })

  const mapTree = (t: Tree): Tree => {
    const out: Tree = {}
    const keys = Object.keys(t)
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const node = t[key]
      const newValues = (node.values || []).map(item => {
        const u = item.unit as number | undefined
        if (u != null && excludedUnits.has(u)) {
          const baseline = baselineByUnit[u] || []
          if (baseline.length === 0) return item
          const q = (typeof item.quantity === 'number' ? item.quantity : 0) / 100
          const scaled = scaleSeries(baseline, q)
          return {...item, periodValues: toPeriodValues(scaled, item.periodValues)}
        }
        return item
      })
      out[key] = {
        header: node.header,
        values: newValues,
        children: mapTree(node.children || {})
      }
    }
    return out
  }
  return mapTree(tree)
}